import {Stack, Typography} from '@mui/material';
import React from 'react';

import {OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {SummaryCard, SummaryCardSection} from 'presentation/components';
import {translations} from 'presentation/translations-new';
import {formatMasks, formatText} from 'presentation/utils';

import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {AppointmenInstructionsAlertInfo} from 'presentation/modules/booking/shared/ui/components/AppointmenInstructionsAlertInfo';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {ReviewCard} from 'presentation/modules/booking/shared/ui/components/ReviewCard';
import {SummaryDistrictSection} from 'presentation/modules/booking/shared/ui/components/SummaryDistrictSection';
import {useExamSummarySectionGroup} from './hooks/useExamSummarySectionGroup';
import {ExamSummarySectionGroupProps} from './types';

const {appointmentCity} = translations['pt-br'].privateFlow.profile;
const {appointmentSummary, beneficiaryData, cardCode} =
	translations['pt-br'].privateFlow.bookingFlow;
const {attendanceUnit, exam, contactInfo, beneficiaryPlan} = translations['pt-br'].commons;
const {appointmentInstructions} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails;

export function ExamSummarySectionGroup({
	stepLabels,
	activeStep,
	handleChangeModals,
	disableGutters,
	showTitle,
	noBorderRadius,
}: ExamSummarySectionGroupProps) {
	const {
		showSpecialtyField,
		showLocationField,
		showUnitField,
		getLocationPrimaryText,
		getLocationSecondaryText,
		showDistricsField,
		bookingTheme,
		colors,
		cdUsuario,
		beneficiaryCardPlanData,
		currentEmail,
		currentPhone,
		isLastStep,
		isUsingGeolocation,
		hideChangeActions,
		hideSpecialtyChangeAction,
		unitName,
		unitAddress,
		specialtyCode,
		specialtyName,
		handleChangeLocation,
	} = useExamSummarySectionGroup({
		activeStep,
		stepLabels,
		handleChangeModals,
	});

	if (!isLastStep) {
		return (
			<SummaryCard
				noBorderRadius={noBorderRadius}
				showTitle={showTitle}
				disableGuters={disableGutters}
				title={appointmentSummary}
				hideAction>
				<SummaryCardSection
					hideAction={true}
					disableGutters={disableGutters}
					hasDivider
					subheader={contactInfo}
					primaryText={formatMasks('phone', currentPhone)}
					iconName="phone-android-filled"
					disableSecondaryTextFormat
					secondaryData={{
						primaryText: currentEmail,
						iconName: 'mail-filled',
					}}
				/>
				{showSpecialtyField ? (
					<SummaryCardSection
						hideAction={hideSpecialtyChangeAction}
						handleAction={() => handleChangeModals(ChangeFlowVariantEnum.SPECIALTY)}
						disableGutters={disableGutters}
						hasDivider
						subheader={exam}
						primaryText={specialtyName}
						iconName={bookingTheme.iconName}
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_ESPECIALIDADE}${FlowEnum.EXAM}`}
					/>
				) : null}

				{showLocationField ? (
					<SummaryCardSection
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_LOCAL_DE_ATENDIMENTO}${FlowEnum.EXAM}`}
						hideAction={hideChangeActions}
						handleAction={handleChangeLocation}
						disableGutters={disableGutters}
						hasDivider
						subheader={appointmentCity}
						primaryText={getLocationPrimaryText()}
						disablePrimaryTextFormat={isUsingGeolocation}
						secondaryText={getLocationSecondaryText()}
						iconName="my-location-filled"
					/>
				) : null}

				{showDistricsField ? (
					<SummaryDistrictSection
						flowType={FlowEnum.EXAM}
						dataTagId={`${OdontoBookingTaggingEnum.CLICK_ALTERAR_BAIRRO}${FlowEnum.EXAM}`}
					/>
				) : null}

				{showUnitField ? (
					<SummaryCardSection
						hideAction={hideChangeActions}
						handleAction={() => handleChangeModals(ChangeFlowVariantEnum.UNIT)}
						disableGutters={disableGutters}
						hasDivider
						subheader={attendanceUnit}
						primaryText={unitName}
						iconName={'location-on-filled'}
						secondaryText={unitAddress}
					/>
				) : null}
			</SummaryCard>
		);
	}

	return (
		<Stack gap={2}>
			<Typography variant="subtitle2" color={colors.light.text.secondary}>
				{beneficiaryData}
			</Typography>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={beneficiaryPlan} />
				<Stack gap={'16px'}>
					<ReviewCard.Content
						iconName={'account-circle-filled'}
						primaryText={formatText(
							beneficiaryCardPlanData.userName,
						)}></ReviewCard.Content>
					<ReviewCard.Content
						iconName={'hapvida-logo'}
						primaryText={formatText(beneficiaryCardPlanData.planName)}
						secondaryText={`${cardCode}${cdUsuario}`}></ReviewCard.Content>
				</Stack>
			</ReviewCard.Root>
			<ReviewCard.Root>
				<ReviewCard.Header subheader={contactInfo} />
				<Stack gap={'8px'}>
					<ReviewCard.Content
						iconName={'phone-android-filled'}
						primaryText={formatMasks('phone', currentPhone)}></ReviewCard.Content>
					<ReviewCard.Content
						iconName={'mail-filled'}
						primaryText={currentEmail}
						disablePrimaryTextFormat></ReviewCard.Content>
				</Stack>
			</ReviewCard.Root>
			<Stack gap={2}>
				<Typography variant="subtitle2" color={colors.light.text.secondary}>
					{appointmentInstructions.title}
				</Typography>
				<AppointmenInstructionsAlertInfo
					bookingType={BookingTypeEnum.EXAM}
					specialtyCode={specialtyCode}
					specialtyName={specialtyName}
				/>
			</Stack>
		</Stack>
	);
}
